import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { onIdTokenChanged, type User } from "firebase/auth";
import { useNavigate, useParams } from "react-router-dom";
import { api, auth, friendlyError } from "./api";
import { useI18n } from "./i18n";
import { ErrorBox, Loading } from "./ui";
import { themeVariables } from "./theme";
import type { Workspace, RecordData, Collection } from "./types";
const AuthContext = createContext<{
  user: User | null;
  loading: boolean;
  workspaces: Workspace[];
  refresh: () => Promise<void>;
  error: string;
}>({
  user: null,
  loading: true,
  workspaces: [],
  refresh: async () => {},
  error: "",
});
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [error, setError] = useState("");
  const accountRequest = useRef(0);
  const identityGeneration = useRef(0);
  const identityUid = useRef<string | null>(null);
  const refresh = useCallback(async () => {
    const run = ++accountRequest.current;
    const uid = auth.currentUser?.uid;
    if (!auth.currentUser) {
      setWorkspaces([]);
      return;
    }
    try {
      const result = await api<{ workspaces: Workspace[] }>("/me");
      if (run === accountRequest.current && auth.currentUser?.uid === uid) {
        setWorkspaces(result.workspaces);
        setError("");
      }
    } catch (e) {
      if (run === accountRequest.current && auth.currentUser?.uid === uid)
        setError(friendlyError(e));
      throw e;
    }
  }, []);
  useEffect(
    () =>
      onIdTokenChanged(auth, async (value) => {
        const generation = ++identityGeneration.current;
        const changed = identityUid.current !== (value?.uid || null);
        identityUid.current = value?.uid || null;
        if (changed) {
          accountRequest.current++;
          setWorkspaces([]);
          setError("");
          setLoading(true);
        }
        setUser(value);
        if (value) {
          await refresh().catch(() => {});
        } else {
          accountRequest.current++;
          setWorkspaces([]);
          setError("");
        }
        if (generation === identityGeneration.current) setLoading(false);
      }),
    [refresh],
  );
  return (
    <AuthContext.Provider value={{ user, loading, workspaces, refresh, error }}>
      {children}
    </AuthContext.Provider>
  );
}
export const useAuth = () => useContext(AuthContext);
const WorkspaceContext = createContext<{
  workspace: Workspace;
  setWorkspace: (w: Workspace) => void;
  refresh: () => Promise<void>;
} | null>(null);
export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { wid } = useParams();
  const { user } = useAuth();
  const uid = user?.uid;
  const scope = `${uid || ""}/${wid || ""}`;
  const activeScope = useRef(scope);
  activeScope.current = scope;
  const [loadedScope, setLoadedScope] = useState("");
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { t } = useI18n();
  const requestGeneration = useRef(0);
  const refresh = useCallback(async () => {
    const run = ++requestGeneration.current;
    if (!wid || !uid || auth.currentUser?.uid !== uid) return;
    try {
      const result = await api<{ workspace: Workspace }>(`/workspaces/${wid}`);
      if (
        run === requestGeneration.current &&
        activeScope.current === scope &&
        auth.currentUser?.uid === uid
      ) {
        setWorkspace(result.workspace);
        setLoadedScope(scope);
        setError("");
      }
    } catch (e) {
      if (
        run === requestGeneration.current &&
        activeScope.current === scope &&
        auth.currentUser?.uid === uid
      ) {
        setWorkspace(null);
        setError(friendlyError(e));
      }
    }
  }, [wid, uid, scope]);
  useEffect(() => {
    setWorkspace(null);
    void refresh();
    window.addEventListener("focus", refresh);
    return () => {
      requestGeneration.current++;
      window.removeEventListener("focus", refresh);
    };
  }, [refresh]);
  useEffect(() => {
    if (!wid || !uid) return;
    let disconnect: (() => void) | undefined;
    function syncVisibility() {
      if (document.visibilityState === "hidden") {
        if (disconnect) {
          disconnect();
          disconnect = undefined;
          requestGeneration.current++;
        }
        return;
      }
      if (disconnect) return;
      const controller = new AbortController();
      let stopped = false;
      let timer: ReturnType<typeof setTimeout> | undefined;
      let delay = 500;
      async function connect() {
        try {
          const token = await auth.currentUser?.getIdToken();
          if (
            !token ||
            stopped ||
            activeScope.current !== scope ||
            auth.currentUser?.uid !== uid
          )
            return;
          const response = await fetch(`/api/workspaces/${wid}/events`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          });
          if (
            stopped ||
            activeScope.current !== scope ||
            auth.currentUser?.uid !== uid
          )
            return;
          if (
            response.status === 401 ||
            response.status === 403 ||
            response.status === 404
          ) {
            setWorkspace(null);
            setError(
              t(
                "Your access to this workspace has changed.",
                "הגישה שלכם למרחב זה השתנתה.",
              ),
            );
            return;
          }
          if (!response.ok || !response.body)
            throw new Error("Connection unavailable");
          delay = 500;
          await refresh();
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          while (!stopped) {
            const result = await reader.read();
            if (
              stopped ||
              result.done ||
              activeScope.current !== scope ||
              auth.currentUser?.uid !== uid
            )
              break;
            buffer += decoder
              .decode(result.value, { stream: true })
              .replace(/\r/g, "");
            let end: number;
            while ((end = buffer.indexOf("\n\n")) >= 0) {
              const message = buffer.slice(0, end);
              buffer = buffer.slice(end + 2);
              const data = message
                .split("\n")
                .filter((line) => line.startsWith("data:"))
                .map((line) => line.slice(5).trim())
                .join("\n");
              if (data) {
                try {
                  const event = JSON.parse(data);
                  const next = event.workspace || event;
                  if (next.id === wid && typeof next.version === "number")
                    setWorkspace((previous) =>
                      !previous || next.version >= previous.version
                        ? { ...previous, ...next }
                        : previous,
                    );
                  else if (event.type === "revoked") {
                    setWorkspace(null);
                    setError(
                      t("Workspace access was removed.", "הגישה למרחב הוסרה."),
                    );
                    return;
                  }
                } catch {
                  void refresh();
                }
              }
            }
          }
        } catch {
          if (stopped) return;
        }
        if (!stopped) {
          timer = setTimeout(connect, delay);
          delay = Math.min(delay * 2, 30000);
        }
      }
      void connect();
      disconnect = () => {
        stopped = true;
        controller.abort();
        clearTimeout(timer);
      };
    }
    document.addEventListener("visibilitychange", syncVisibility);
    syncVisibility();
    return () => {
      document.removeEventListener("visibilitychange", syncVisibility);
      disconnect?.();
    };
  }, [wid, uid, scope, refresh, t("en", "he")]);
  if (loadedScope !== scope && !error) return <Loading />;
  if (error)
    return (
      <main className="narrow standalone">
        <ErrorBox error={error} retry={() => void refresh()} />
        <button className="button" onClick={() => navigate("/app")}>
          {t("Choose a workspace", "בחירת מרחב")}
        </button>
      </main>
    );
  if (!workspace || workspace.id !== wid) return <Loading />;
  return (
    <WorkspaceContext.Provider
      value={{
        workspace,
        setWorkspace: (next) => {
          if (activeScope.current === scope && auth.currentUser?.uid === uid)
            setWorkspace(next);
        },
        refresh,
      }}
    >
      <div
        className="workspace-theme"
        style={themeVariables(workspace.primaryColor, workspace.secondaryColor)}
      >
        {children}
      </div>
    </WorkspaceContext.Provider>
  );
}
export function useWorkspace() {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error("Workspace context missing");
  return value;
}
export function useCollection<T extends RecordData>(
  collection: Collection,
  query = "",
) {
  const { workspace } = useWorkspace();
  const { user } = useAuth();
  const uid = user?.uid;
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cursor, setCursor] = useState<string | null>(null);
  const generation = useRef(0);
  const refresh = useCallback(async () => {
    const run = ++generation.current;
    setLoading(true);
    try {
      const result = await api<{ items: T[]; nextCursor: string | null }>(
        `/workspaces/${workspace.id}/${collection}?limit=100${query ? "&" + query : ""}`,
      );
      if (run === generation.current && auth.currentUser?.uid === uid) {
        setItems(result.items);
        setCursor(result.nextCursor);
        setError("");
      }
    } catch (e) {
      if (run === generation.current && auth.currentUser?.uid === uid)
        setError(friendlyError(e));
    } finally {
      if (run === generation.current && auth.currentUser?.uid === uid)
        setLoading(false);
    }
  }, [workspace.id, uid, collection, query]);
  useEffect(() => {
    void refresh();
    window.addEventListener("heritage-refresh", refresh);
    return () => {
      generation.current++;
      window.removeEventListener("heritage-refresh", refresh);
    };
  }, [refresh]);
  async function more() {
    if (!cursor || loadingMoreRef.current) return;
    const run = generation.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const result = await api<{ items: T[]; nextCursor: string | null }>(
        `/workspaces/${workspace.id}/${collection}?limit=100&cursor=${encodeURIComponent(cursor)}${query ? "&" + query : ""}`,
      );
      if (run === generation.current && auth.currentUser?.uid === uid) {
        setItems((old) => [
          ...old,
          ...result.items.filter(
            (item) => !old.some((existing) => existing.id === item.id),
          ),
        ]);
        setCursor(result.nextCursor);
        setError("");
      }
    } catch (e) {
      if (run === generation.current && auth.currentUser?.uid === uid)
        setError(friendlyError(e));
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }
  return {
    items,
    loading,
    loadingMore,
    error,
    refresh,
    more,
    hasMore: !!cursor,
    setItems,
  };
}
